import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/ui/utils/tailwind';
import { Text, UnstyledButton } from '@/shared/ui';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { isSameDay } from '@features/agenda/utils/agenda-dates';
import { eventColor } from './event-color';
import { isAllDayEvent } from './time-grid';
import { calendarDisplayName } from '@features/agenda/utils/calendar-display';

interface AgendaEventListProps {
  anchor: Date;
  events: ExpandedEvent[];
  calendars: Calendar[];
  onSelect: (event: ExpandedEvent) => void;
}

/**
 * Desktop "Lista" view: chronological list of upcoming events grouped by
 * day, rendered as its own exclusive full-screen view (mutually exclusive
 * with the Day/Week/Month grid views).
 *
 * @param props Anchor date, events, calendars, and select handler.
 * @returns Rendered desktop event list.
 */
export const AgendaEventList: React.FC<AgendaEventListProps> = ({
  anchor,
  events,
  calendars,
  onSelect,
}) => {
  const { t } = useTranslation('features/agenda');
  const days = Array.from({ length: 7 }, (_, i) =>
    dayjs(anchor).add(i, 'day').toDate(),
  );

  const hasDayEvents = days.some((day) =>
    events.some((e) => isSameDay(e.start_at_utc, day)),
  );

  if (!hasDayEvents) return null;

  return (
    <div
      data-testid="agenda-desktop-event-list"
      className="h-full overflow-y-auto rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-(--space-4)"
    >
      <Text className="mb-(--space-3) text-(--text-body-sm) font-semibold text-[var(--text-secondary)]">
        {t('desktop_list.title')}
      </Text>
      {days.map((day) => {
        const dayEvents = events
          .filter((e) => isSameDay(e.start_at_utc, day))
          .sort((a, b) => a.start_at_utc.localeCompare(b.start_at_utc));
        if (dayEvents.length === 0) return null;
        const today = dayjs().isSame(dayjs(day), 'day');
        return (
          <div key={dayjs(day).format('YYYY-MM-DD')} className="mb-(--space-4) last:mb-0">
            <DayHeader day={day} isToday={today} t={t} />
            <div className="flex flex-col gap-(--space-1-5) pl-(--space-4)">
              {dayEvents.map((ev) => (
                <EventRow
                  key={`${ev.event_id}-${ev.occurrence_start}`}
                  event={ev}
                  calendars={calendars}
                  onClick={() => onSelect(ev)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Renders the sticky date label above a day's grouped events.
 */
function DayHeader({ day, isToday, t }: { day: Date; isToday: boolean; t: (k: string) => string }) {
  const d = dayjs(day);
  return (
    <div className="mb-(--space-1-5) flex items-center gap-(--space-2)">
      <div
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold',
        )}
        style={{
          background: isToday ? 'var(--seed-base)' : 'var(--surface-secondary)',
          color: isToday ? '#fff' : 'var(--text-primary)',
        }}
      >
        {d.format('D')}
      </div>
      <Text className="text-xs font-semibold capitalize text-[var(--text-primary)]">
        {d.format('ddd')}
      </Text>
      <Text className="text-xs text-[var(--text-tertiary)]">
        {d.format('D MMM')}
      </Text>
    </div>
  );
}

interface EventRowProps {
  event: ExpandedEvent;
  calendars: Calendar[];
  onClick: () => void;
}

/**
 * Renders a single agenda event as a clickable, calendar-colored row.
 */
function EventRow({ event, calendars, onClick }: EventRowProps) {
  const { t } = useTranslation('features/agenda');
  const color = eventColor(event, calendars);
  const calendar = calendars.find((c) => c.calendar_id === event.calendar_id);
  const calName = calendar ? calendarDisplayName(calendar, t) : '';

  return (
    <UnstyledButton
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-(--space-3) rounded-(--radius-md) px-(--space-2) py-(--space-1-5) text-left transition-colors hover:bg-[var(--surface-secondary)]"
    >
      <div
        className="h-3 w-3 shrink-0 rounded-full"
        style={{ background: color }}
      />
      <Text className="shrink-0 text-xs font-medium text-[var(--text-secondary)]" style={{ minWidth: 40 }}>
        {isAllDayEvent(event) ? t('drawer.all_day') : dayjs(event.start_at_utc).format('HH:mm')}
      </Text>
      {/* Title in the event's colour and bold, matching the chips in the day,
          week and month views. Inline, because the shared Text component's own
          font-weight class outranks `font-bold` at equal specificity. */}
      <Text
        className="min-w-0 flex-1 truncate text-(--text-body-sm)"
        style={{ color, fontWeight: 700 }}
      >
        {event.title}
      </Text>
      <Text className="shrink-0 text-xs text-[var(--text-tertiary)]">
        {calName}
      </Text>
    </UnstyledButton>
  );
}
